import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from 'antd';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { useGetTemplatesQuery, useGetStateDepartmentsQuery } from '../../../features/platform/platformApi';
import { usePlatformState } from '../../../features/platform/platformStateSlice';

/* ------------------------------------------------------------------ *
 * Platform Admin — Add department · step 1 (`isPick`).
 * Choose a library template (→ /admin/departments/add/:code) or a
 * state-only hierarchy (→ the adhoc flow). Reads the active state.
 * ------------------------------------------------------------------ */

export default function AddDepartmentPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { code: stateCode, name: stateName } = usePlatformState();

  const { data: allTemplates = [], isLoading } = useGetTemplatesQuery();
  const { data: stateDepartments = [] } = useGetStateDepartmentsQuery(stateCode);

  const templates = useMemo(() => {
    const q = query.trim().toLowerCase();
    const enabled = new Set(stateDepartments.map((d) => d.code));
    return allTemplates.filter(
      (t) => !enabled.has(t.code) && (!q || t.name.toLowerCase().includes(q)),
    );
  }, [query, allTemplates, stateDepartments]);

  return (
    <div className="flex flex-col gap-[18px]">
      <Breadcrumbs
        items={[
          { label: 'All states', to: '/admin/overview' },
          { label: `Departments in ${stateName}`, to: '/admin/departments' },
          { label: 'Add a department' },
        ]}
      />

      <section className="flex flex-col gap-3.5 rounded-xl border border-line/30 bg-white p-6 shadow-card">
        <div>
          <div className="text-xs text-ink-secondary">STEP 1 OF 2 · CHOOSE A TEMPLATE</div>
          <h1 className="mb-1.5 mt-1 text-2xl font-semibold text-ink">Add a department</h1>
          <p className="max-w-[760px] text-sm text-ink-secondary">
            Templates come with levels, bindings and designations preset. Enabling one adds it to the
            {` ${stateName} `}profile, ready to customise and map. You choose how much to include next.
          </p>
        </div>

        <div className="flex max-w-[420px] flex-col gap-1">
          <label className="text-xs font-semibold text-ink-secondary">Search templates</label>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. Water, Forest, Health"
            size="large"
            allowClear
          />
        </div>

        {isLoading && <div className="text-sm text-ink-secondary">Loading templates…</div>}

        <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-2.5">
          <button
            type="button"
            onClick={() => navigate('/admin/departments/add/adhoc')}
            className="flex min-h-[112px] flex-col gap-1.5 rounded-lg border border-dashed border-brand bg-brand-shade/60 p-4 text-left transition-colors hover:bg-brand-shade"
          >
            <span className="text-[11px] text-brand-dark">NO TEMPLATE</span>
            <span className="text-[15px] font-bold text-ink">{stateName}-only hierarchy</span>
            <span className="text-xs text-[#434655]">
              For a department only {stateName} has. Starts anchored at State and District, with one
              own level.
            </span>
          </button>
          {templates.map((t) => (
            <button
              key={t.code}
              type="button"
              onClick={() => navigate(`/admin/departments/add/${t.code}`)}
              className="flex min-h-[112px] flex-col gap-1.5 rounded-lg border border-line bg-white p-4 text-left transition-colors hover:border-[#bfdbfe] hover:bg-brand-bg"
            >
              <span className="text-[11px] text-ink-secondary">
                {t.code} · {t.control}
              </span>
              <span className="text-[15px] font-bold text-ink">{t.name}</span>
              <span className="text-xs text-[#434655]">{t.level_count} levels</span>
            </button>
          ))}
        </div>
        {!isLoading && templates.length === 0 && (
          <div className="text-[13px] text-ink-secondary">
            No template matches that search. Use the {stateName}-only hierarchy.
          </div>
        )}
      </section>
    </div>
  );
}
